/**
 * Route → capability map for the client-side mirror of the capability gates.
 *
 * Roles grant surfaces through `kind='capability'` RBAC categories (see
 * docs/Architecture/RBAC/CAPABILITIES.md). The Sidebar hides a tab whose
 * capability the user lacks; `VisibilityGuard` (App.tsx) uses this module to
 * redirect a typed URL the same way. Mirrors `CAPABILITIES[].tabPath` in
 * src/auth/rbac/capabilities.ts — the server gates are the real boundary.
 */
export const CAPABILITY_ROUTES: ReadonlyArray<{ path: string; capability: string }> = [
  { path: '/', capability: 'overview-access' },
  { path: '/briefings', capability: 'briefings-access' },
  { path: '/my-salesforce', capability: 'my-salesforce-access' },
  { path: '/clinical', capability: 'clinical-access' },
  { path: '/clinical-success', capability: 'clinical-success-access' },
  { path: '/finance', capability: 'finance-access' },
  { path: '/accounts', capability: 'accounts-access' },
  { path: '/scout', capability: 'scout-access' },
  { path: '/meetings', capability: 'meetings-access' },
  // Most specific first: '/dashboards/files' before '/dashboards'.
  { path: '/dashboards/files', capability: 'files-access' },
  { path: '/dashboards', capability: 'dashboards-access' },
  { path: '/data-explorer', capability: 'business-access' },
  { path: '/operations', capability: 'operations-access' },
  { path: '/product', capability: 'product-access' },
  { path: '/personal', capability: 'personal-space-access' },
  { path: '/settings', capability: 'my-settings-access' },
  { path: '/compass-ai', capability: 'compass-ai-access' },
];

/** Capability a pathname needs, or undefined when the path is not capability-gated (e.g. /admin/*). */
export function capabilityForRoute(pathname: string): string | undefined {
  for (const r of CAPABILITY_ROUTES) {
    // '/' is exact-match only — otherwise every path would resolve to Overview.
    if (r.path === '/') {
      if (pathname === '/') return r.capability;
      continue;
    }
    if (pathname === r.path || pathname.startsWith(r.path + '/')) return r.capability;
  }
  return undefined;
}

/** Preferred landing order when the requested route is not allowed. */
const FALLBACK_ORDER = ['/', '/dashboards', '/briefings', '/accounts', '/my-salesforce', '/dashboards/files', '/settings'];

/** First capability-gated route the user may open; `/settings` as the last resort. */
export function firstAllowedRoute(capabilities: ReadonlySet<string>): string {
  for (const p of FALLBACK_ORDER) {
    const cap = capabilityForRoute(p);
    if (cap && capabilities.has(cap)) return p;
  }
  return '/settings';
}
